/* eslint-disable max-len */
import { useState, useEffect } from 'react';

import { useMsal } from '@azure/msal-react';
import { b2cPolicies } from '../auth/authConfig';

import {
	getCustomerData,
	customerAddressCreate,
	updateCustomerAddress,
	updateCustomerDefaultAddress,
	deleteCustomerAddress
} from '../account';

const isBrowser = typeof window !== `undefined`;
const loginLocalStorageKey = `shopify_customer_access_token`;

const defaultValues = {
	customer: {
		loading: true,
		isLoggedIn: false,
	},
};

const useCustomer = () => {
	const { instance, accounts, inProgress } = useMsal();
	const [customer, setCustomer] = useState(defaultValues.customer);
	const [isLoginDisabled] = useState(false);
	const [triggerSilentSSO, setTriggerSilentSSO] = useState(true);

	const getMsalUserAccount = () => {
		if (!accounts.length) return null;
		const {signUp, signIn, editEmail} = b2cPolicies.names;
		const msalUserAccount = accounts.find(account => account.idTokenClaims.acr === editEmail.toLowerCase())
							|| accounts.find(account => account.idTokenClaims.acr === signUp.toLowerCase())
							|| accounts.find(account => account.idTokenClaims.acr === signIn.toLowerCase());
		return msalUserAccount;
	};

	const getMsalRoleAccount = () => {
		if (!accounts.length) return null;
		const editRolePolicyName = b2cPolicies.names.editRole.toLowerCase();
		const msalRoleAccount = accounts.find(account => account.idTokenClaims.acr === editRolePolicyName);
		return msalRoleAccount;
	};

	const resetCustomer = (options = {}) => {
		const {loading = false} = options;
		setCustomer(() => ({
			loading,
			isLoggedIn: false,
		}));
	};

	const signUpUser = () => {
		instance.loginRedirect({
			authority: b2cPolicies.authorities.signUp.authority,
		}).catch(error => window.console.log(error));
	};

	const loginUser = () => {
		instance.loginRedirect().catch(error => window.console.log(error));
	};

	const logoutUser = options => {
		const {
			redirect = true,
			postLogoutRedirectUri = `https://${ process.env.GATSBY_SHOPIFY_STORE_URL }/account/logout?return_url=${ globalThis?.location?.href }`,
		} = options || {};

		localStorage.removeItem(loginLocalStorageKey);
		resetCustomer({loading: true});
		instance.logoutRedirect({
			onRedirectNavigate: () => redirect,
			postLogoutRedirectUri,
		});
	};

	const handleRoleButtonClick = () => {
		instance.loginRedirect({
			authority: b2cPolicies.authorities.editRole.authority,
		});
	};

	const handleEmailButtonClick = () => {
		instance.loginRedirect({
			authority: b2cPolicies.authorities.editEmail.authority,
		});
	};

	const getUserIDToken = async() => {
		try {
			const account = getMsalUserAccount();
			const authorityName = Object.entries(b2cPolicies.names).find(([, value]) => account.idTokenClaims.acr === value)?.[0];
			const res = await instance.acquireTokenSilent({
				account,
				scopes: [],
				authority: b2cPolicies.authorities[authorityName]?.authority,
			});
			return res.idToken;
		} catch (err) {
			window.console.error(err);
			throw new Error('retrieve ID Token from MSAL failed');
		}
	};

	const generateCustomerShopifyAccessToken = async() => {
		const msalUserAccount = getMsalUserAccount();
		const existingCustomerAccessToken = isBrowser
			? localStorage.getItem(loginLocalStorageKey)
			: null;

		if (existingCustomerAccessToken
			&& msalUserAccount.idTokenClaims?.acr.toLowerCase() !== b2cPolicies.names.editEmail.toLowerCase()
		) {
			resetCustomer({loading: true});
			try {
				const token = JSON.parse(existingCustomerAccessToken);
				const today = Date.now();
				const expireDate = new Date(token.expiresAt).getTime();
				const isExpired = expireDate < today;
				if (!isExpired) return token;
			} catch (err) {
				window.console.error('retrieve shopify access token failed: ', err);
				throw new Error('retrieve shopify access token from browser failed');
			}
		}

		try {
			const myHeaders = new Headers();
			myHeaders.append('Content-Type', 'application/json');

			const userIDToken = await getUserIDToken();
			const body = JSON.stringify({
				id_token: userIDToken,
			});

			const requestOptions = {
				method: 'POST',
				headers: myHeaders,
				body,
				redirect: 'follow',
			};

			const json = await fetch(process.env.GATSBY_SSO_GENERATE_SHOPIFY_ACCESS_TOKEN, requestOptions)
				.then(response => {
					return response.json();
				});
			if (!json?.success) throw new Error('retrieve shopify access token from SSO Multipass Func endpoint failed');
			const {access_token: accessToken, expires_at: expiresAt} = json.data;
			const token = {accessToken, expiresAt};
			localStorage.setItem(loginLocalStorageKey, JSON.stringify(token));
			return token;
		} catch (err) {
			localStorage.removeItem(loginLocalStorageKey);
			window.console.error(err);
			throw new Error(err.message);
		}
	};

	const getFullName = shopifyCustomer => {
		const msalUserAccount = getMsalUserAccount();
		const first = shopifyCustomer.firstName || msalUserAccount?.idTokenClaims.given_name;
		const last = shopifyCustomer.lastName || msalUserAccount?.idTokenClaims.family_name;
		if (!first && !last) return null;
		return `${ first || '' } ${ last || '' }`.trim();
	};

	const getRoleInfo = () => {
		const msalUserAccount = getMsalUserAccount();
		const msalRoleAccount = getMsalRoleAccount();
		const role = ['Customer'];
		let roleMessage;

		if (msalUserAccount?.idTokenClaims.extension_isEducator === 'true') role.unshift('Educator');

		// role message
		switch (true) {
		case msalRoleAccount?.idTokenClaims.userMessage:
			roleMessage = msalRoleAccount.idTokenClaims.userMessage;
			break;
		case msalUserAccount?.idTokenClaims.extension_isEducator === 'pending':
			roleMessage = 'Your request for the Educator role is pending. You will be notified by email within 72 hours. Please log out and log back in to see if approval has been granted.';
			break;
		default:
			break;
		}

		const roleInfo = {
			role: role.join(', '),
			roleMessage,
		};
		return roleInfo;
	};

	const handleCustomerShopifyData = (shopifyCustomer, customerAccessToken) => {
		if (!shopifyCustomer) return resetCustomer();
		const shopifyId = shopifyCustomer.shopify_id.substring(23);
		const fullName = getFullName(shopifyCustomer);
		const roleInfo = getRoleInfo();

		setCustomer(prev => ({
			...prev,
			...shopifyCustomer,
			shopify_id: shopifyId,
			customerAccessToken,
			fullName,
			roleInfo,
			isLoggedIn: true,
			loading: false,
		}));
	};

	const initializeCustomer = async() => {
		if (inProgress !== 'none') return;
		if (triggerSilentSSO && !accounts.length && !customer.isLoggedIn) {
			try {
				await instance.ssoSilent({scopes: []});
			} catch (error) {
				// window.console.error('error loging in the user silently:', error, error.message);
				localStorage.removeItem(loginLocalStorageKey);
				resetCustomer();
			} finally {
				setTriggerSilentSSO(() => false);
			}
			return;
		}
		if (!accounts.length) {
			localStorage.removeItem(loginLocalStorageKey);
			resetCustomer();
			return;
		}
		if (customer.isLoggedIn) return;
		try {
			resetCustomer({loading: true});
			const customerShopifyAccessToken = await generateCustomerShopifyAccessToken();
			const customerShopifyData = await getCustomerData(customerShopifyAccessToken?.accessToken);
			handleCustomerShopifyData(customerShopifyData.data.customer, customerShopifyAccessToken);
			setTriggerSilentSSO(() => false);
		} catch (err) {
			resetCustomer();
			// window.alert(`Login error: ${ err.message }`); // eslint-disable-line
			window.console.error(err);
		}
	};

	const createAddress = async(address, useAsDefault = false) => {
		const response = await customerAddressCreate(
			address,
			customer.customerAccessToken.accessToken
		);

		// Address create errors
		if (response.data.customerAddressCreate.customerUserErrors.length > 0) {
			return new Promise((resolve, reject) => {
				reject(response.data.customerAddressCreate.customerUserErrors);
			});
		}

		// If address is to be used as the default address
		if (useAsDefault) {
			const res = await updateCustomerDefaultAddress(
				response.data.customerAddressCreate.customerAddress.id,
				customer.customerAccessToken.accessToken
			);

			return new Promise((resolve, reject) => {
				if (res.data.customerDefaultAddressUpdate.customerUserErrors.length > 0) {
					reject(res.data.customerDefaultAddressUpdate.customerUserErrors);
				}

				setCustomer(prev => ({
					...prev,
					...res.data.customerDefaultAddressUpdate.customer, // updates defaultAddress, addresses
				}));

				resolve(res.data.customerDefaultAddressUpdate);
			});
		}

		// create address without default
		return new Promise((resolve, reject) => { // eslint-disable-line
			const newAddress = {
				node: {
					...response.data.customerAddressCreate.customerAddress,
				},
			};

			setCustomer(prev => ({
				...prev,
				addresses: {
					edges: [
						...prev.addresses.edges,
						newAddress,
					],
				},
			}));

			resolve(response.data.customerAddressCreate.customerAddress);
		});
	};

	const updateAddress = async(id, address, useAsDefault = false) => {
		const response = await updateCustomerAddress(
			id,
			address,
			customer.customerAccessToken.accessToken
		);

		// Address update errors
		if (response.data.customerAddressUpdate.customerUserErrors.length > 0) {
			return new Promise((resolve, reject) => { // eslint-disable-line
				reject(response.data.customerAddressUpdate.customerUserErrors);
			});
		}

		// If address is to be used as the default address
		if (useAsDefault) {
			const res = await updateCustomerDefaultAddress(
				response.data.customerAddressUpdate.customerAddress.id,
				customer.customerAccessToken.accessToken
			);

			return new Promise((resolve, reject) => {
				if (res.data.customerDefaultAddressUpdate.customerUserErrors.length > 0) {
					reject(res.data.customerDefaultAddressUpdate.customerUserErrors);
				}

				setCustomer(prev => ({
					...prev,
					...res.data.customerDefaultAddressUpdate.customer, // updates defaultAddress, addresses
				}));

				resolve(res.data.customerDefaultAddressUpdate);
			});
		}

		const res = await getCustomerData(customer.customerAccessToken.accessToken);

		// create address without default
		return new Promise(resolve => {
			setCustomer(prev => ({
				...prev,
				addresses: res.data.customer.addresses,
				defaultAddress: {...res.data.customer.defaultAddress},
			}));

			resolve(response.data.customerAddressUpdate.customerAddress);
		});
	};

	const removeAddress = async id => {
		const response = await deleteCustomerAddress(
			id,
			customer.customerAccessToken.accessToken
		);

		if (response.data.customerAddressDelete.deletedCustomerAddressId) {
			const res = await getCustomerData(customer.customerAccessToken.accessToken);

			setCustomer(prev => ({
				...prev,
				addresses: res.data.customer.addresses,
				defaultAddress: {...res.data.customer.defaultAddress},
			}));
		}
	};

	useEffect(() => {
		initializeCustomer();
	}, [inProgress, accounts, customer.isLoggedIn]); // eslint-disable-line

	return {
		getUserIDToken,
		customer,
		setCustomer,
		loginUser,
		logoutUser,
		signUpUser,
		handleRoleButtonClick,
		handleEmailButtonClick,
		createAddress,
		updateAddress,
		removeAddress,
		isLoginDisabled,
	};
};

export default useCustomer;
